import { useRef, useState, type ReactElement } from 'react';
import type { BoardVisibility, BoardVisibilityPatch } from '@molt/shared';
import { Button, Dialog, Menu, Notice, type MenuItem } from '@/components';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * `FE-09.1` — the board visibility picker. `D4-BRIEF.md` §6.4 is the contract.
 *
 * ---------------------------------------------------------------------------
 * ⚠ FOUR VALUES, THREE OFFERED — AND THE FOURTH BREAKS THE RADIO SILENTLY
 * ---------------------------------------------------------------------------
 * `enterprise_visible` is a real wire value a board can hold (`FR-PERM-011`,
 * Parity-Later, owned by no D4 item). `Menu`'s `selection: 'radio'` matches
 * `value` against item ids, so a board holding it against three offered items
 * matches **nothing**: no item renders selected, nothing throws, and the row
 * reads as *"this board has no visibility"*.
 *
 * So the held value is rendered as text from a **total** map over all four
 * values. B67 promotes the Enterprise value from display-only to an offered
 * item; keeping the map total still makes a future visibility-level change a
 * compile error instead of a half-rendered menu.
 *
 * ⚠ **Locked, never hidden** (`FR-PERM-012` AC1: *"the visibility control is
 * visible but not actionable"*). `canChange: false` renders the control
 * `disabled` with its `disabledReason` through `Button` — which carries it as a
 * `Tooltip` **and** an `aria-describedby`, because a disabled control is not
 * hoverable-only. It is never removed from the DOM.
 *
 * ---------------------------------------------------------------------------
 * WHY THE TRIGGER IS OUTSIDE `Menu` WHEN LOCKED, NOT A DISABLED ITEM INSIDE IT
 * ---------------------------------------------------------------------------
 * A disabled `Button` (`canChange: false`) renders a `Tooltip`-wrapped
 * fragment plus a sibling `sr-only` reason span — two DOM nodes, not one —
 * whenever `disabledReason` is shown. `Menu`'s trigger goes through Radix's
 * `asChild`, which requires exactly one element. Passing that fragment as the
 * trigger would break silently on the first disabled render. There is also
 * nothing to open: a locked control has no menu to show. So the locked branch
 * renders the `Button` standalone, never wrapped in `Menu`; only the
 * `canChange: true` branch — where `Button` is never disabled and always
 * renders a single control — wraps it as the trigger.
 *
 * ---------------------------------------------------------------------------
 * THE CONFIRMATION NEVER MOVES THE RADIO BEFORE IT RESOLVES
 * ---------------------------------------------------------------------------
 * The radio section's `value` is `props.visibility`, read directly — never
 * mirrored into local state. Choosing "Public" opens the confirmation dialog
 * and calls `onChange` only on confirm; the section's `value` has not moved,
 * so the picker keeps showing the old level for exactly as long as the caller
 * has not applied a new one. Cancel calls nothing at all. `FR-PERM-010` AC3.
 */
export interface BoardVisibilityControlProps {
  visibility: BoardVisibility;
  /** False → rendered, disabled, `disabledReason` shown. Never hidden. §2.1 */
  canChange: boolean;
  /** Non-null exactly when `canChange` is false. */
  disabledReason: string | null;
  /** Called only after the world-readable confirmation for `public`. */
  onChange: (patch: BoardVisibilityPatch) => void;
  busy?: boolean | undefined;
  /** The server's message for a failed change, rendered in this row. */
  error?: string | undefined;
  className?: string;
  /**
   * `FE-67.1` / `FR-PERM-011` AC1 — *"Given a board in a workspace not linked
   * to an Enterprise, when its admin opens the visibility picker, then this
   * level is not offered."*
   *
   * `GET /workspaces/:id/enterprise` (`workspaceEnterpriseLinkageSchema`,
   * `D1`) is the fact this prop carries: `enterpriseId === null` means the
   * workspace has no Enterprise, so `enterprise_visible` is unreachable and
   * must not be offered. Before that route existed the frontend had no way to
   * learn it, which is why the picker offered all four unconditionally.
   *
   * ✅ **Wired.** `BoardSettingsSection` forwards it and `BoardMenuPanel`
   * derives it from that route, gated on the panel being open. The prop is no
   * longer prop-only; `FR-PERM-011.spec.ts` asserts the suppressed picker in a
   * browser.
   *
   * ⚠ **THE "FOUR CALLERS" THIS DOCSTRING USED TO NAME WERE THREE FILES THAT
   * NEVER RENDERED THIS COMPONENT.** Corrected against a measurement rather
   * than deleted, because the mistake is an easy one to make twice: the earlier
   * list counted *importers of this module* and there are four of those, but
   * `BoardFace`, `BoardMenuPanel` and `TemplateVisibilityControls` import only
   * the label and consequence key tables, to NAME a held value in an
   * announcement string. Scanning for the JSX open tag instead of for the
   * module name finds exactly **one** product render site in the repository —
   * `BoardSettingsSection.tsx` — plus this file's own sibling oracle.
   * `TemplateVisibilityControls` could not consume the prop even if it were
   * passed one: its `TEMPLATE_VISIBILITY_OPTIONS` is three values and
   * `enterprise_visible` is not among them, so it has no row to suppress.
   *
   * ⚠ **STILL DEFAULTS TO `true`, AND THE DEFAULT IS STILL THE WHOLE
   * POLARITY.** The reason has changed shape but not force: `undefined` must
   * read as *permissive* so that any future caller which forgets the prop
   * renders the four-level picker rather than a silently narrowed one. A
   * predicate written the other way round would suppress the Enterprise row
   * across the product with nothing red anywhere — the case that goes red first
   * is the sibling oracle's *"undefined is permissive"*.
   *
   * Declared `| undefined` and passed plainly. A conditional JSX spread is a
   * hole in `tsc`, not a style choice.
   */
  offerEnterpriseVisibility?: boolean | undefined;
}

/**
 * ---------------------------------------------------------------------------
 * ⚠ `FE-80.1` — TWO TABLES OF KEYS, AND TWO ENGLISH TABLES THAT NOTHING
 *    RENDERS. THE ENGLISH ONES ARE A PROJECTION, NOT AN AUTHOR.
 * ---------------------------------------------------------------------------
 * A module constant cannot call `t()` (`WorkspaceSwitcher`'s ruling), so the
 * `_KEYS` tables below hold **catalogue keys** and every render site resolves
 * them — here, and in `TemplateVisibilityControls`, `BoardMenuPanel` and
 * `BoardFace`, the four files on the path.
 *
 * `VISIBILITY_LABELS` and `VISIBILITY_CONSEQUENCES` survive with their bytes
 * untouched for exactly one reason: `BoardVisibilityControl.test.tsx` imports
 * them and uses their **values as expected rendered text**, which makes it this
 * file's byte-preservation oracle. Nothing in product code reads them any more.
 * Two authors of eight strings is the `boardMap.viewLabel` defect unless
 * something forbids the drift, so `board-templates.routing.test.ts` asserts
 * each arm byte-equal to the catalogue value its `_KEYS` twin points at, and
 * asserts source-level that no render site here subscripts either English
 * table. (That sentence deliberately does not spell the subscript out: a pin
 * that forgets to strip comments would then match its own warning and go red
 * on correct code.)
 */

/** Total over all four values; B67 offers Enterprise as a normal picker value. */
export const VISIBILITY_LABELS: Record<BoardVisibility, string> = {
  private: 'Private',
  workspace_visible: 'Workspace',
  enterprise_visible: 'Enterprise',
  public: 'Public',
};

/** The catalogue keys the four labels above are the English projection of. */
export const VISIBILITY_LABEL_KEYS: Record<BoardVisibility, string> = {
  private: 'boardTemplates.boardVisibility.private',
  workspace_visible: 'boardTemplates.boardVisibility.workspace',
  enterprise_visible: 'boardTemplates.boardVisibility.enterprise',
  public: 'boardTemplates.boardVisibility.public',
};

/**
 * `FE-09.1`'s own item text: *"each level's consequence stated in the UI, not a
 * help article"*. Derived from the server's own `.describe()` prose on
 * `boardVisibilityPatchSchema`, which is the most authoritative copy in the
 * repository for this surface.
 */
export const VISIBILITY_CONSEQUENCES: Record<BoardVisibility, string> = {
  private: "Only people on this board's member list can see it.",
  workspace_visible:
    'Everyone in the workspace can see this board. Guests see only the boards they were added to.',
  enterprise_visible: 'Anyone in the Enterprise can see this board.',
  public: 'Anyone on the internet can see this board, signed in or not.',
};

/** The catalogue keys the four consequences above are the English projection of. */
export const VISIBILITY_CONSEQUENCE_KEYS: Record<BoardVisibility, string> = {
  private: 'boardTemplates.boardVisibility.consequence.private',
  workspace_visible: 'boardTemplates.boardVisibility.consequence.workspace',
  enterprise_visible: 'boardTemplates.boardVisibility.consequence.enterprise',
  public: 'boardTemplates.boardVisibility.consequence.public',
};

/** The four levels this control offers, in this order. */
export const OFFERED_VISIBILITIES = [
  'private',
  'workspace_visible',
  'enterprise_visible',
  'public',
] as const;

type OfferedVisibility = (typeof OFFERED_VISIBILITIES)[number];

export function BoardVisibilityControl({
  visibility,
  canChange,
  disabledReason,
  onChange,
  busy = false,
  error,
  className,
  offerEnterpriseVisibility = true,
}: BoardVisibilityControlProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const label = t(VISIBILITY_LABEL_KEYS[visibility]);
  const accessibleName = t('boardTemplates.boardVisibility.trigger', { label });

  function handleSelect(next: OfferedVisibility) {
    switch (next) {
      case 'public':
        // Never called directly — the confirmation gates it. §5.8, §6.4.
        setConfirmOpen(true);
        return;
      case 'private':
        onChange({ visibility: 'private' });
        return;
      case 'workspace_visible':
        onChange({ visibility: 'workspace_visible' });
        return;
      case 'enterprise_visible':
        onChange({ visibility: 'enterprise_visible' });
        return;
    }
  }

  function handleConfirm() {
    setConfirmOpen(false);
    onChange({ visibility: 'public', confirmWorldReadable: true });
  }

  function handleCancel() {
    // Nothing is emitted; the picker still shows the old value because
    // `value` above is read from `props.visibility`, which never moved.
    setConfirmOpen(false);
  }

  /**
   * -------------------------------------------------------------------------
   * ⚠ SUPPRESSION IS CONDITIONAL ON THE HELD VALUE, AND THAT IS THE TRAP
   * -------------------------------------------------------------------------
   * `FR-PERM-011`'s own edge case: *"If a workspace is unlinked from the
   * Enterprise, boards at this level must fall back to a defined narrower
   * level rather than silently becoming public."* Until something performs
   * that fallback, a board can hold `enterprise_visible` while its workspace
   * carries no `enterprise_id` — exactly the state this prop reports.
   *
   * Dropping the row there would put `Menu`'s `selection: 'radio'` back in the
   * state this file's opening docstring exists to warn about: `value` matches
   * no item id, no item renders selected, nothing throws, and the row reads as
   * *"this board has no visibility"*. So the held value is always offered, and
   * suppression applies only to a level the board does not currently hold.
   *
   * ⚠ `OFFERED_VISIBILITIES` is NOT edited. It stays exported, byte-unchanged
   * and total over `BoardVisibility` — `board-templates.routing.test.ts` sorts
   * it and asserts it equals the whole enum, and this file's sibling oracle
   * counts menu rows against its length. The offered list is *derived* from
   * it and the flag, never by narrowing the constant.
   *
   * Removed, not disabled-with-a-reason: AC1 says *"not offered"*, and a
   * reason would need an interface-language key this chunk cannot declare.
   */
  const suppressEnterprise =
    !offerEnterpriseVisibility && visibility !== 'enterprise_visible';
  const offered: readonly OfferedVisibility[] = suppressEnterprise
    ? OFFERED_VISIBILITIES.filter((value) => value !== 'enterprise_visible')
    : [...OFFERED_VISIBILITIES];

  const items: MenuItem[] = offered.map((value) => ({
    id: value,
    // Level name first in DOM order, so the accessible name (the
    // concatenated text content) begins with the visible label — WCAG 2.5.3.
    //
    // -----------------------------------------------------------------------
    // ⚠ `whitespace-normal` IS LOAD-BEARING, AND THE DEFECT IT FIXES WAS
    //    RECORDED WITH THE WRONG CAUSE
    // -----------------------------------------------------------------------
    // Fan-out 12's screenshot pass photographed the three consequence
    // sentences ellipsised — *"Only people on this board's member list can see
    // i…"* — and returned it as residual 4, diagnosed as *"the open menu is
    // anchored to a trigger inside the right-hand panel, so its consequence
    // lines clip at the viewport's right edge."* **That cause is wrong**, and
    // acting on it would have moved a popover that is placed correctly:
    // `Menu` already passes `collisionPadding={12}` to Radix.
    //
    // The real cause is one utility in `Menu.tsx`: every item's label is
    // wrapped in `min-w-0 flex-1 truncate`, and Tailwind's `truncate` is
    // `overflow:hidden; text-overflow:ellipsis; white-space:nowrap`. The
    // inherited `nowrap` flattens this two-line label onto one row, where the
    // 20rem content cap plus `overflow:hidden` cuts it. It would clip
    // identically in the middle of a 4K screen — the viewport was never
    // involved.
    //
    // ⚠ **`Menu.tsx` is deliberately NOT edited.** `truncate` is right for
    // every other menu in the product, all of which pass a single-line label;
    // this is the one caller that passes a paragraph, so this is where the
    // override belongs. `whitespace-normal` re-enables wrapping for this
    // subtree only, the sentence wraps inside the 20rem cap, and the row grows
    // because `Menu`'s item is `items-center`, not a fixed height.
    //
    // ⚠ Its oracle is the capture frame, not the unit suite: jsdom has no
    // layout, so a `getBoundingClientRect` assertion here would be vacuously
    // green. The sibling oracle pins only that the class is still present,
    // which is a regression tripwire and not a proof of wrapping.
    label: (
      <span className="flex flex-col items-start gap-0.5 py-0.5 text-left whitespace-normal">
        <span className="text-card text-ink">{t(VISIBILITY_LABEL_KEYS[value])}</span>
        <span className="text-xs text-ink-muted">
          {t(VISIBILITY_CONSEQUENCE_KEYS[value])}
        </span>
      </span>
    ),
    // The actual change is wired through the section's own `onValueChange`
    // below — `WorkspaceMemberList`'s shipped precedent for a radio `Menu`.
    onSelect: () => undefined,
  }));

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {canChange ? (
        <Menu
          trigger={
            <Button
              type="button"
              variant="secondary"
              aria-label={accessibleName}
              busy={busy}
            >
              {label}
            </Button>
          }
          sections={[
            {
              id: 'visibility',
              selection: 'radio',
              value: visibility,
              onValueChange: (value) => {
                handleSelect(value as OfferedVisibility);
              },
              items,
            },
          ]}
        />
      ) : (
        <Button
          type="button"
          variant="secondary"
          aria-label={accessibleName}
          disabled
          disabledReason={
            disabledReason ?? t('boardTemplates.boardVisibility.lockedReason')
          }
        >
          {label}
        </Button>
      )}

      {/*
       * ---------------------------------------------------------------------
       * ⚠ THE CURRENT LEVEL'S CONSEQUENCE, RENDERED FOR EVERY VIEWER — FOUND BY
       *    THE `D4` SCREENSHOT PASS, WITH EVERY TEST GREEN
       * ---------------------------------------------------------------------
       * The three consequences above are inside the `Menu`'s items, which is
       * where `FE-09.1`'s *"each level's consequence stated in the UI, not a
       * help article"* is met for somebody **choosing** a level. It is not met
       * for somebody **reading** one — and a normal member cannot open that
       * menu at all, because their trigger is `disabled`.
       *
       * So at 1440×900 the locked row was a greyed chip reading `Workspace`
       * and nothing else: the consequence unreachable behind a disabled menu,
       * and the lock's reason reachable only through a `Tooltip` and an
       * `aria-describedby`. `FR-PERM-025` exists so that *"everyone can find
       * out the rules they are working under"*, and for exactly the population
       * it names, the surface said almost nothing.
       *
       * Every assertion stayed green throughout — the control **is** present,
       * **is** disabled, and its reason **is** programmatically reachable.
       * Role and accessible-name queries cannot see that a word on its own
       * means nothing to the person reading it.
       *
       * One line, from the same map the menu items use, so the two spellings
       * cannot drift.
       */}
      <span className="text-xs text-ink-muted">
        {t(VISIBILITY_CONSEQUENCE_KEYS[visibility])}
      </span>

      {error !== undefined ? <Notice tone="bad" body={error} /> : null}

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        title={t('boardTemplates.boardVisibility.confirmTitle')}
        size="sm"
        initialFocus={cancelRef}
        footer={
          <>
            <Button type="button" variant="primary" onClick={handleConfirm}>
              {t('boardTemplates.boardVisibility.confirm')}
            </Button>
            <Button type="button" variant="quiet" ref={cancelRef} onClick={handleCancel}>
              {t('boardTemplates.boardVisibility.cancel')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-muted">
          {t('boardTemplates.boardVisibility.confirmBody')}
        </p>
      </Dialog>
    </div>
  );
}
